import { useMemo } from 'react';
import { useEChart } from './useEChart.js';
import { ChartFrame } from './ChartFrame.js';
import { formatChartAmount, formatCountFa } from './formatters.js';
import type { ChartBaseStateProps, ChartDatum } from './types.js';

export interface DonutChartProps extends ChartBaseStateProps {
  readonly data: readonly ChartDatum[];
  readonly ariaLabel: string;
  readonly height?: number | undefined;
  readonly centerLabel?: string | undefined;
  readonly valueFormatter?: ((value: number) => string) | undefined;
}

/**
 * Categorical proportions (a status breakdown) with the legend always on —
 * identity never rides on hover. Colors from the fixed
 * `--sk-chart-{1,6,3,2,4,5}` order; center graphic shows the real total.
 */
export function DonutChart({
  data,
  ariaLabel,
  height = 280,
  centerLabel = 'مجموع',
  amountUnit,
  valueFormatter: valueFormatterProp,
  isLoading,
  loadingMessage,
  error,
  status,
  errorMessage,
  onRetry,
  retryLabel,
  emptyMessage,
}: DonutChartProps) {
  // Stable identity: a fresh default function on every render is a changed
  // `useEChart` input and would repaint (and re-animate) the chart each time.
  const defaultFormatter = useMemo(
    () => (amountUnit ? (v: number) => formatChartAmount(v, amountUnit) : formatCountFa),
    [amountUnit],
  );
  const valueFormatter = valueFormatterProp ?? defaultFormatter;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const hasError = Boolean(error || (status && status >= 400));
  const isEmpty = !isLoading && !hasError && (data.length === 0 || total === 0);
  const containerRef = useEChart(
    (theme) => ({
      tooltip: {
        trigger: 'item',
        valueFormatter: (v: number) => valueFormatter(Number(v)),
      },
      legend: {
        bottom: 0,
        left: 'center',
        textStyle: { color: theme.text, fontSize: 12 },
        icon: 'circle',
        itemWidth: 10,
        itemHeight: 10,
      },
      graphic: {
        elements: [
          {
            type: 'text',
            left: 'center',
            top: '38%',
            style: {
              text: valueFormatter(total),
              fill: theme.text,
              fontSize: 22,
              fontWeight: 700,
              textAlign: 'center',
            },
          },
          {
            type: 'text',
            left: 'center',
            top: '48%',
            style: {
              text: centerLabel,
              fill: theme.textMuted,
              fontSize: 12,
              textAlign: 'center',
            },
          },
        ],
      },
      series: [
        {
          type: 'pie',
          radius: ['58%', '78%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: theme.surface, borderWidth: 2 },
          label: { show: false },
          labelLine: { show: false },
          data: data.map((d, i) => ({
            name: d.label,
            value: d.value,
            itemStyle: { color: theme.categorical[i % theme.categorical.length] },
          })),
        },
      ],
    }),
    [data, centerLabel, valueFormatter],
  );

  return (
    <ChartFrame
      containerRef={containerRef}
      height={height}
      ariaLabel={ariaLabel}
      isLoading={isLoading}
      loadingMessage={loadingMessage}
      error={error}
      status={status}
      errorMessage={errorMessage}
      onRetry={onRetry}
      retryLabel={retryLabel}
      isEmpty={isEmpty}
      emptyMessage={emptyMessage}
    />
  );
}
